import path from "path";
import { IFile, SUB_TYPE, IPage } from "./interfaces";
import { FILE_TYPES } from "./types";

export class File implements IFile {
  id: string;
  filePath?: string;
  type?: FILE_TYPES;
  subType: SUB_TYPE;
  sequence?: number;
  numPages: number;
  pages: IPage[];
  name: string;
  extension: string;
  oriName: string;
  nameWithoutRegexMatch?: string;
  removeFromFileRegex?: string;
  maskedOffMatches: any[];
  subSequence: number | null;
  loggingName: string;
  uniqueId?: string;

  constructor(params: IFile, config: any) {
    this.id = params?.id ?? "";
    this.filePath = params?.filePath;
    this.type = params?.type?.toLowerCase() as FILE_TYPES;
    this.sequence = params?.sequence;
    this.pages = params?.pages ?? [];
    this.numPages = params?.numPages ?? this.pages.length;
    this.maskedOffMatches = [];
    this.oriName = params?.name;
    this.uniqueId = this.getUniqueId(this.oriName, config);
    this.subSequence = this.getSubSequence(params?.name, config);
    this.name = this.initName(this.oriName, config);
    this.loggingName = this.getLoggingName(config?.regex?.removeFromFileNameForLogging);
    this.extension = path.parse(this.oriName || "").ext.toLowerCase();
    this.subType = SUB_TYPE.none;
  }

  getName(includeExtension = false) {
    if (includeExtension) return `${this.name}${this.extension}`;
    return this.name;
  }

  getLoggingName(removeRegex?: string) {
    let name = this.oriName;
    if (removeRegex) {
      const regex = new RegExp(removeRegex, "g");
      const matches = name.matchAll(regex);

      if (matches) {
        for (const match of matches) {
          name = name.replace(match[0], "");
        }
      }
    }
    return name;
  }

  initName(oriName: string, config: any) {
    let name = path.parse(oriName || "").name.toLowerCase();

    if (this.uniqueId && config?.upl?.removeFromSplitFiles) {
      const regex = new RegExp(config.upl.removeFromSplitFiles, "gi");
      const matches = name.matchAll(regex);

      if (matches) {
        for (const match of matches) {
          this.maskedOffMatches.push({
            match: match[0],
            index: match.index,
            length: match[0].length,
          });
          name = name.replace(match[0], "");
        }
      }
    }

    if (config?.regex?.removeFromFileName) {
      const regex = new RegExp(config?.regex.removeFromFileName, "gi");
      const matches = name.matchAll(regex);

      if (matches) {
        for (const match of matches) {
          this.maskedOffMatches.push({
            match: match[0],
            index: match.index,
            length: match[0].length,
          });
          name = name.replace(match[0], "");
        }
      }
    }

    return name;
  }

  getUniqueId(oriName: string, config: any) {
    const name = path.parse(oriName || "").name.toLocaleLowerCase();
    if (config?.mode === "uploadPortal" && config?.upl?.uniqueIdOfSplitFiles) {
      const regex = new RegExp(config.upl.uniqueIdOfSplitFiles, "gi");
      const match = name.match(regex);
      if (match) {
        return match[0];
      }
    }
    return undefined;
  }

  getSubSequence(oriName: string, config: any) {
    const name = path.parse(oriName || "").name.toLocaleLowerCase();
    if (this.uniqueId && (config?.regex?.subSequence || config?.upl?.subSequence)) {
      const regex = new RegExp(config.regex.subSequence || config?.upl?.subSequence, "gi");
      const match = name.match(regex);
      if (match) {
        return parseInt(match[0]);
      }
    }
    return null;
  }

  isBody() {
    if (this.type === FILE_TYPES.body) {
      return true;
    }
    return false;
  }

  isCover() {
    if (this.type === FILE_TYPES.cover) {
      return true;
    }
    return false;
  }

  isAssigned() {
    if (this.type && Object.keys(FILE_TYPES).includes(this.type)) {
      return true;
    }
    return false;
  }

  hasSequence() {
    if (typeof this.sequence !== "undefined") return true;
    return false;
  }

  hasPdfProperties() {
    if (!this.numPages) return false;
    if (!this.getWidth() || !this.getHeight()) return false;
    return true;
  }

  getNameHonoringSequence() {
    if (typeof this.sequence !== "undefined") {
      return setNumberWithZerosInFront(this.sequence, 4) + "_" + this.oriName;
    } else {
      return this.oriName;
    }
  }

  setWidth(value: number, pageRange?: number[]) {
    for (const _p in this.pages) {
      const page = this.pages[_p];
      const pageNum = parseFloat(_p) + 1;

      if (typeof pageRange === "undefined" || pageRange?.includes(pageNum)) {
        page.width = value;
      }
    }
  }

  setHeight(value: number, pageRange?: number[]) {
    for (const _p in this.pages) {
      const page = this.pages[_p];
      const pageNum = parseFloat(_p) + 1;

      if (typeof pageRange === "undefined" || pageRange?.includes(pageNum)) {
        page.height = value;
      }
    }
  }

  getWidth(_page = 1) {
    const width = this.pages.find((page) => page.page === _page)?.width;
    if (width) return width;
    return 0;
  }

  getHeight(_page = 1) {
    const height = this.pages.find((page) => page.page === _page)?.height;
    if (height) return height;
    return 0;
  }

  isPageSizeEqual() {
    let isEqual = true;
    const width = this.getWidth();
    const height = this.getHeight();

    for (const page of this.pages) {
      if (width !== page.width) {
        isEqual = false;
      }
      if (height !== page.height) {
        isEqual = false;
      }
    }
    return isEqual;
  }

  addPage(page: IPage) {
    this.pages.push(page);
  }
}

function setNumberWithZerosInFront(num: number, size: number): string {
  const s = "000000000" + num;
  return s.substring(s.length - size);
}
